import { useState } from 'react';
import { useDeleteInvitedUser } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { useDialog } from '@spendbrain/common/hooks';
import { Invitee } from '@spendbrain/types/user';

interface UseRemoveAccessDialogProps {
  onRemove?: (invitee: Invitee) => void;
}

export const useRemoveAccessDialog = ({
  onRemove,
}: UseRemoveAccessDialogProps = {}) => {
  const { handleClose, handleOpen, isOpen } = useDialog();
  const [inviteeToRemove, setInviteeToRemove] = useState<Invitee | null>(null);
  const { open } = useNotification();
  const { mutateAsync: deleteInvitedUser, isPending: loading } =
    useDeleteInvitedUser({
      onError: () => {
        open({
          type: 'error',
          message: t('components.shareDialog.removeAccess.errorMessage', {
            name: inviteeToRemove?.name,
          }),
        });
      },
      onSuccess: () => {
        open({
          type: 'success',
          message: t('components.shareDialog.removeAccess.successMessage', {
            name: inviteeToRemove?.name,
          }),
        });
      },
    });
  const { t } = useTranslation();

  const onRemoveClick = (invitee: Invitee) => {
    setInviteeToRemove(invitee);
    handleOpen();
  };

  const onConfirm = async () => {
    if (!inviteeToRemove?.oid) return;
    await deleteInvitedUser({ userId: inviteeToRemove.oid });
    onRemove?.(inviteeToRemove);

    handleClose();
  };

  return {
    onClose: handleClose,
    onCancel: handleClose,
    onConfirm,
    open: isOpen,
    onOpenDialog: onRemoveClick,
    invitee: inviteeToRemove,
    loading,
  };
};
